import { useMemo, useState } from "preact/hooks"
import { Bar, CartesianGrid, Cell, ComposedChart, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { BASELINE_COLOR, row_tooltip, DataTable, Figure, GRID_COLOR, InlineBar, Legend, MUTED_COLOR, Panel, WORKING_COLOR, column } from "./trade_review_parts"
import {
  ALL_PRODUCTS,
  DEFAULT_FAMILY,
  DEFAULT_RETAILER,
  curve_row,
  depth_row,
  event_row,
  family_choices,
  fmt_day,
  fmt_int,
  fmt_pct0,
  fmt_signed_pct,
  fmt_usd2,
  fmt_usd_short,
  promo_window,
  retailer_choices,
  scorecard_row,
  search_events,
  trade_review,
  view_lift,
  weekly_row,
  window_weeks,
} from "./trade_review_model"

function WeeklyPromoChart({ data }: { data: trade_review }) {
  const [retailer, set_retailer] = useState(DEFAULT_RETAILER)
  const [family, set_family] = useState(DEFAULT_FAMILY)
  const retailers = useMemo(() => retailer_choices(data.weekly), [data.weekly])
  const families = useMemo(() => family_choices(data.weekly, retailer), [data.weekly, retailer])
  const rows = useMemo(() => data.weekly.filter((row: weekly_row) => row.retailer === retailer && row.family === family), [data.weekly, retailer, family])
  const windows = useMemo(() => data.promo_windows.filter((row: promo_window) => row.retailer === retailer && row.family === family), [data.promo_windows, retailer, family])
  const weeks = useMemo(() => rows.map((row: weekly_row) => row.week), [rows])
  const lift = view_lift(rows)

  function pick_retailer(next: string) {
    set_retailer(next)
    if (!family_choices(data.weekly, next).includes(family)) set_family(ALL_PRODUCTS)
  }

  return (
    <Panel
      title="Weekly shipments with each promo laid on top"
      note="Bars are units KeHE shipped to the retailer each week. Shaded bands are promo windows from billbacks, or the calendar where no billback line exists. The dashed line is the baseline from nearby non-promo weeks.">
      <div class="flex flex-wrap items-end gap-3">
        <div class="space-y-1">
          <Label>Retailer</Label>
          <Select value={retailer} onValueChange={pick_retailer}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>{retailers.map((name: string) => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div class="space-y-1">
          <Label>Product</Label>
          <Select value={family} onValueChange={set_family}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>{families.map((name: string) => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p class="text-sm text-muted-foreground pb-2">
          {lift ? `${fmt_signed_pct(lift.lift)} against baseline across ${lift.weeks} promo weeks` : "No promo weeks in this view"}
        </p>
      </div>
      <div class="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 22, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="week" tickFormatter={fmt_day} minTickGap={28} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={fmt_int} width={48} tick={{ fontSize: 12 }} />
            {windows.map((row: promo_window) => {
              const span = window_weeks(weeks, row.start, row.end)
              if (!span) return null
              const label = row.rate == null ? undefined : { value: `${fmt_usd2(row.rate)} off`, position: "insideTop" as const, fontSize: 11, offset: -16 }
              return <ReferenceArea key={`${row.start}${row.end}`} x1={span.first} x2={span.last} fill={WORKING_COLOR} fillOpacity={0.1} ifOverflow="extendDomain" label={label} />
            })}
            <Tooltip
              content={row_tooltip((row: weekly_row) => [`Week of ${fmt_day(row.week)}`, `Shipped ${fmt_int(row.units)}`, `Ordered ${fmt_int(row.ordered)}`, ...(row.baseline == null ? [] : [`Baseline ${fmt_int(row.baseline)}`]), ...(row.on_promo ? ["On promo"] : [])]) as never}
            />
            <Bar dataKey="units" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {rows.map((row: weekly_row) => <Cell key={row.week} fill={row.on_promo ? WORKING_COLOR : MUTED_COLOR} />)}
            </Bar>
            <Line dataKey="baseline" stroke={BASELINE_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Legend items={[{ label: "Promo weeks", color: WORKING_COLOR }, { label: "Other weeks", color: MUTED_COLOR }, { label: "Baseline", color: BASELINE_COLOR, dashed: true }]} />
    </Panel>
  )
}

function curve_week(rel: number): string {
  return rel >= 0 ? String(rel + 1) : String(rel)
}

function PromoShapeChart({ data }: { data: trade_review }) {
  return (
    <Panel
      title="What a four-week promo looks like"
      note={<>Average of four- and five-week promos, indexed so baseline is 100. Shipments step up in the first promo week and return to normal within about two weeks of the end. The model finds the week after still running <Figure summary={data.summary} field="post_week" as="signed_pct" />, so we see no sign of volume being borrowed from later weeks.</>}>
      <div class="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.event_curve} margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="rel" type="number" domain={["dataMin", "dataMax"]} ticks={data.event_curve.map((row: curve_row) => row.rel)} tickFormatter={curve_week} tick={{ fontSize: 12 }} label={{ value: "Weeks before the promo, then weeks from its start", position: "insideBottom", offset: -10, fontSize: 12 }} />
            <YAxis domain={[80, 135]} width={40} tick={{ fontSize: 12 }} />
            <ReferenceArea x1={0} x2={3} fill={WORKING_COLOR} fillOpacity={0.1} />
            <ReferenceLine y={100} stroke={BASELINE_COLOR} />
            <Tooltip
              content={row_tooltip((row: curve_row) => [row.rel >= 0 ? `Promo week ${row.rel + 1}` : `${-row.rel} weeks before`, `Index ${fmt_int(row.idx)}`, `${fmt_int(row.n)} promos`]) as never}
            />
            <Line dataKey="idx" stroke={WORKING_COLOR} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  )
}

function RetailerLiftRanges({ rows }: { rows: scorecard_row[] }) {
  const low = Math.min(...rows.map((row: scorecard_row) => Math.min(row.model_lo, row.lift))) - 0.03
  const high = Math.max(...rows.map((row: scorecard_row) => Math.max(row.model_hi, row.lift))) + 0.03

  function left(value: number): string {
    return `${((value - low) / (high - low)) * 100}%`
  }

  return (
    <Panel
      title="Lift by retailer, with its range"
      note="Dots are the model estimate with its 95% range. Diamonds are the simple promo-week comparison. A range that crosses zero means we can't yet rule out no lift.">
      <div class="space-y-2">
        {rows.map((row: scorecard_row) => (
          <div key={row.retailer} class="flex items-center gap-3 text-sm" title={`Lift ${fmt_signed_pct(row.lift)}, model ${fmt_signed_pct(row.model)}, range ${fmt_signed_pct(row.model_lo)} to ${fmt_signed_pct(row.model_hi)}`}>
            <div class="w-44 shrink-0 truncate text-right">{row.retailer}</div>
            <div class="relative h-5 flex-1 min-w-0">
              <div class="absolute inset-y-0 w-px" style={{ left: left(0), background: BASELINE_COLOR }} />
              <div class="absolute top-1/2 h-0.5 -translate-y-1/2 rounded" style={{ left: left(row.model_lo), width: `calc(${left(row.model_hi)} - ${left(row.model_lo)})`, background: WORKING_COLOR }} />
              <div class="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card" style={{ left: left(row.model), background: WORKING_COLOR }} />
              <div class="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-foreground" style={{ left: left(row.lift) }} />
            </div>
            <div class="w-12 shrink-0 text-right tabular-nums">{fmt_signed_pct(row.model)}</div>
          </div>
        ))}
      </div>
    </Panel>
  )
}

const DEPTH_COLUMNS = (top: number): column<depth_row>[] => [
  { header: "Depth", left: true, cell: (row: depth_row) => row.band },
  { header: "Promos", cell: (row: depth_row) => fmt_int(row.events) },
  { header: "Lift", left: true, cell: (row: depth_row) => <InlineBar share={row.lift / top} label={fmt_signed_pct(row.lift)} /> },
  { header: "Trade billed", cell: (row: depth_row) => fmt_usd_short(row.spend) },
  { header: "Extra sales", cell: (row: depth_row) => fmt_usd_short(row.inc_rev) },
  { header: "Trade per $1 extra sales", cell: (row: depth_row) => fmt_usd2(row.per) },
]

const EVENT_COLUMNS: column<event_row>[] = [
  { header: "Retailer", left: true, cell: (row: event_row) => row.retailer },
  { header: "Products", left: true, cell: (row: event_row) => row.groups },
  { header: "Start", left: true, cell: (row: event_row) => fmt_day(row.start) },
  { header: "Weeks", cell: (row: event_row) => fmt_int(row.weeks) },
  { header: "Off per unit", cell: (row: event_row) => (row.rate == null ? "" : fmt_usd2(row.rate)) },
  { header: "Base units/wk", cell: (row: event_row) => fmt_int(row.base_wk) },
  { header: "Promo units/wk", cell: (row: event_row) => fmt_int(row.promo_wk) },
  { header: "Lift", cell: (row: event_row) => fmt_signed_pct(row.lift) },
  { header: "Fill", cell: (row: event_row) => fmt_pct0(row.fill) },
  { header: "Extra sales", cell: (row: event_row) => fmt_usd_short(row.inc_rev) },
  { header: "Spend", cell: (row: event_row) => (row.spend == null ? "" : fmt_usd_short(row.spend)) },
  { header: "Spend from", left: true, cell: (row: event_row) => row.spend_src },
]

export function LiftTab({ data }: { data: trade_review }) {
  const [query, set_query] = useState("")
  const top_lift = Math.max(...data.depth.map((row: depth_row) => row.lift))
  return (
    <div class="space-y-6">
      <WeeklyPromoChart data={data} />
      <div class="grid gap-4 xl:grid-cols-2">
        <PromoShapeChart data={data} />
        <RetailerLiftRanges rows={data.retailer_scorecard} />
      </div>
      <Panel title="Deeper discounts buy more lift at about the same cost" note="Allowance per unit as a share of KeHE's cost for the item, across billback-measured promos.">
        <DataTable columns={DEPTH_COLUMNS(top_lift)} rows={data.depth} row_key={(row: depth_row) => row.band} />
      </Panel>
      <Panel title="Every promo we measured" note="Spend is what KeHE billed. Where only the calendar exists, spend shows the plan and is marked as such. Fill below 80% means shipments may understate demand.">
        <Input className="max-w-xs" placeholder="Search retailer or product" value={query} onInput={(event: Event) => set_query((event.target as HTMLInputElement).value)} />
        <DataTable tall columns={EVENT_COLUMNS} rows={search_events(data.events, query)} row_key={(row: event_row) => row.event} />
      </Panel>
    </div>
  )
}
